class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.openWithFreshData(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.openWithFreshData(cartLink);
      }
    });
  }

  openWithFreshData(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const currentDrawer = document.getElementById('CartDrawer');
        const sourceDrawer = html.getElementById('CartDrawer');
        const currentInner = currentDrawer ? currentDrawer.querySelector('.drawer__inner') : null;
        const sourceInner = sourceDrawer ? sourceDrawer.querySelector('.drawer__inner') : null;
        if (currentInner && sourceInner) {
          currentInner.innerHTML = sourceInner.innerHTML;
        } else {
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        }
        this.classList.remove('is-empty');
        
        try {
          if (typeof window.checkAndAddProtectPlus === 'function') {
            window.checkAndAddProtectPlus();
          } else if (typeof window.addProtectPlusToCart === 'function') {
            window.addProtectPlusToCart();
          }
          if (typeof window.attachEventHandlers === 'function') {
            window.attachEventHandlers();
          }
          if (typeof window.updateDisclaimerVisibility === 'function') {
            window.updateDisclaimerVisibility();
          }
        } catch (hookError) {
          console.error('Error running cart drawer post-refresh hooks:', hookError);
        }
        
        this.updateCartIcon();
        
        this.open(triggeredBy);
      })
      .catch((e) => {
        console.error('Error fetching cart drawer:', e);
        this.open(triggeredBy);
      });
  }

  updateCartIcon() {
    fetch(`${routes.cart_url}?section_id=cart-icon-bubble`)
      .then((response) => response.text())
      .then((responseText) => {
        const cartIcon = document.querySelector('#cart-icon-bubble');
        if (cartIcon) {
          cartIcon.innerHTML = responseText;
        }
      })
      .catch((e) => {
        console.error('Error updating cart icon:', e);
      });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Store the current toggle state before updating the DOM
    let wasToggleEnabled = false;
    const protectPlusToggle = document.querySelector('.protect-plus-toggle');
    if (protectPlusToggle) {
      wasToggleEnabled = protectPlusToggle.classList.contains('enabled');
    } else {
      // If toggle doesn't exist yet, check localStorage
      const enabled = localStorage.getItem('protect_plus_enabled');
      wasToggleEnabled = enabled !== null ? JSON.parse(enabled) : true;
    }
    
    // Update the DOM
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    
    // Safely update sections
    this.getSectionsToRender().forEach((section) => {
      // Check if the section exists in parsedState
      if (parsedState.sections && parsedState.sections[section.id]) {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
          
        if (sectionElement) {
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }
      }
    });

    // PROTECT PLUS LOGIC TO TRIGGER CLICK WHEN CART RENDERED
    // Only check if ProtectPlus should be added after cart is rendered
    setTimeout(() => {
      // Use the checkAndAddProtectPlus function if available
      if (typeof window.checkAndAddProtectPlus === 'function') {
        console.log('Checking if ProtectPlus should be added from cart drawer');
        window.checkAndAddProtectPlus();
      } else if (typeof window.addProtectPlusToCart === 'function') {
        console.log('Using addProtectPlusToCart as fallback');
        window.addProtectPlusToCart();
      }

      // Ensure event handlers and visibility are updated for the current DOM.
      // This runs after checkAndAddProtectPlus has been called.
      // If checkAndAddProtectPlus caused a cart modification and re-render,
      // that new render will also go through this logic.
      if (typeof window.attachEventHandlers === 'function') {
        window.attachEventHandlers();
      }
      if (typeof window.updateDisclaimerVisibility === 'function') {
        window.updateDisclaimerVisibility();
      }
    }, 300);
    

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this.addEventListener('cart:update', this.onCartUpdate.bind(this));
    this.setupSubscriptionHandlers();
  }

  setupSubscriptionHandlers() {
    this.addEventListener('change', (event) => {
      if (event.target.classList.contains('cart-item__subscription-select')) {
        this.handleSubscriptionChange(event);
      }
    });
  }

  handleSubscriptionChange(event) {
    const select = event.target;
    const cartItem = select.closest('tr[id^="CartDrawer-Item-"]');
    if (!cartItem) return;

    const selectedOption = select.options[select.selectedIndex];
    
    const propertyInputs = cartItem.querySelectorAll('input[name^="properties["]');
    propertyInputs.forEach(input => {
      const propertyName = input.name.match(/properties\[(.*?)\]/)[1];
      switch(propertyName) {
        case '_offer_id':
          input.value = selectedOption.dataset.offerId;
          break;
        case '_offer_name':
          input.value = selectedOption.dataset.offerName;
          break;
        case '_offer_price':
          input.value = selectedOption.dataset.offerPrice;
          break;
        case '_shipping_interval_frequency':
          input.value = selectedOption.value;
          break;
        case '_subscription_type':
          input.value = 'subscription';
          break;
      }
    });

    if (propertyInputs.length === 0) {
      const propertiesToAdd = {
        '_offer_id': selectedOption.dataset.offerId,
        '_offer_name': selectedOption.dataset.offerName,
        '_offer_price': selectedOption.dataset.offerPrice,
        '_shipping_interval_frequency': selectedOption.value,
        '_subscription_type': 'subscription'
      };

      Object.entries(propertiesToAdd).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${key}]`;
        input.value = value;
        cartItem.appendChild(input);
      });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  async handleSubscriptionChange(event) {
    const select = event.target;
    const cartItem = select.closest('tr[id^="CartDrawer-Item-"]');
    if (!cartItem) return;

    const lineNumber = parseInt(cartItem.id.split('CartDrawer-Item-')[1]);
    if (isNaN(lineNumber)) {
      console.error('Invalid line number');
      return;
    }

    const quantityInput = cartItem.querySelector('.quantity__input');
    const quantity = quantityInput ? parseInt(quantityInput.value) : 1;

    const selectedOption = select.options[select.selectedIndex];

    try {
      const body = JSON.stringify({
        line: lineNumber,
        quantity: quantity,
        properties: {
          '_offer_id': selectedOption.dataset.offerId,
          '_offer_name': selectedOption.dataset.offerName,
          '_offer_price': selectedOption.dataset.offerPrice,
          '_subscription_type': 'subscription',
          '_shipping_interval_frequency': selectedOption.value
        },
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      });

      this.enableLoading(lineNumber);

      const response = await fetch(routes.cart_change_url, {
        ...fetchConfig(),
        body
      });

      const parsedState = await response.json();
      
      if (parsedState.status === 422 || parsedState.errors) {
        throw new Error(parsedState.description || parsedState.errors || 'Error updating subscription');
      }

      this.getSectionsToRender().forEach((section) => {
        if (parsedState.sections && parsedState.sections[section.section]) {
          const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
          if (elementToReplace) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          }
        }
      });

      const cartDrawer = this.closest('cart-drawer');
      if (cartDrawer) {
        cartDrawer.classList.toggle('is-empty', parsedState.item_count === 0);
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-drawer',
        cartData: parsedState,
        variantId: select.dataset.variantId
      });

    } catch (error) {
      console.error('Error updating subscription:', error);
      const errorContainer = cartItem.querySelector('.cart-item__error');
      if (errorContainer) {
        errorContainer.querySelector('.cart-item__error-text').textContent = error.message || 'Failed to update subscription. Please try again.';
        errorContainer.classList.remove('hidden');
      }
    } finally {
      this.disableLoading(lineNumber);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const section = doc.querySelector(selector);
    return section ? section.innerHTML : '';
  }

  onCartUpdate(event) {
    // Store the toggle state before cart update
    const protectPlusToggle = document.querySelector('.protect-plus-toggle');
    if (protectPlusToggle) {
      this.wasToggleEnabled = protectPlusToggle.classList.contains('enabled');
      console.log('CartDrawerItems: Stored toggle state before update:', this.wasToggleEnabled ? 'enabled' : 'disabled');
    }
  }
  
  renderContents(parsedState) {
    try {
      // Call the parent renderContents method
      super.renderContents(parsedState);
      
      // Restore the toggle state after rendering
      if (typeof this.wasToggleEnabled !== 'undefined' && window.variantStrings && window.variantStrings.protectPlus) {
        console.log('CartDrawerItems: Restoring toggle state after update:', this.wasToggleEnabled ? 'enabled' : 'disabled');
        setTimeout(() => {
          if (typeof simulateClickOnProtectPlusToggle === 'function') {
            simulateClickOnProtectPlusToggle(this.wasToggleEnabled);
          }
        }, 100); // Increased timeout to ensure DOM is fully updated
      }
    } catch (error) {
      console.error('Error in CartDrawerItems.renderContents:', error);
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
